import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Plus, Loader2 } from 'lucide-react'
import { useProjectStore } from '../../stores/projectStore'

interface BulkAddScenesDialogProps {
  open: boolean
  onClose: () => void
  projectId: string
}

const BulkAddScenesDialog: React.FC<BulkAddScenesDialogProps> = ({
  open,
  onClose,
  projectId
}) => {
  const [numScenes, setNumScenes] = useState(5)
  const [isCreating, setIsCreating] = useState(false)
  const { createScene, scenes } = useProjectStore()

  const handleCreate = async () => {
    if (numScenes < 1 || numScenes > 50) return

    setIsCreating(true)
    
    try {
      const startIndex = scenes.length
      
      // Create scenes sequentially to ensure proper ordering
      for (let i = 0; i < numScenes; i++) {
        const sceneNumber = startIndex + i + 1
        await createScene({
          projectId,
          title: `${sceneNumber}`,
          description: '',
          orderIndex: startIndex + i,
        })
      }
      
      onClose()
    } catch (error) {
      console.error('Failed to create scenes:', error)
      alert('Failed to create scenes. Please try again.')
    } finally {
      setIsCreating(false)
    }
  }

  const handleIncrement = () => {
    if (numScenes < 50) setNumScenes(numScenes + 1)
  }

  const handleDecrement = () => {
    if (numScenes > 1) setNumScenes(numScenes - 1)
  }

  if (!open) return null

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="glass-card w-full max-w-md border border-white/20"
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary-500/20 rounded-full flex items-center justify-center">
                <Plus className="w-5 h-5 text-primary-300" />
              </div>
              <h2 className="text-xl font-headline text-white">Add Multiple Scenes</h2>
            </div>
            <button
              onClick={onClose}
              className="glass-button p-2 hover:bg-white/20"
              disabled={isCreating}
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            <div className="text-center">
              <p className="text-white/70 font-body mb-6">
                How many scenes would you like to add?
              </p>

              {/* Number Input with Controls */}
              <div className="flex items-center justify-center space-x-4">
                <button
                  onClick={handleDecrement}
                  disabled={numScenes <= 1 || isCreating}
                  className="glass-button w-12 h-12 flex items-center justify-center text-white text-2xl hover:bg-glass-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  −
                </button>

                <div className="relative">
                  <input
                    type="number"
                    value={numScenes}
                    onChange={(e) => {
                      const val = parseInt(e.target.value)
                      if (!isNaN(val) && val >= 1 && val <= 50) {
                        setNumScenes(val)
                      }
                    }}
                    min="1"
                    max="50"
                    disabled={isCreating}
                    className="glass-card bg-white/5 border border-white/20 text-white text-4xl font-headline font-bold text-center w-32 h-20 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:opacity-50"
                  />
                </div>

                <button
                  onClick={handleIncrement}
                  disabled={numScenes >= 50 || isCreating}
                  className="glass-button w-12 h-12 flex items-center justify-center text-white text-2xl hover:bg-glass-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  +
                </button>
              </div>

              <p className="text-white/50 text-sm font-body mt-4">
                Scenes will be named: {scenes.length + 1}, {scenes.length + 2}, {scenes.length + 3}...
              </p>
            </div>

            {/* Info */}
            <div className="glass-card bg-blue-500/10 border border-blue-500/20 p-4">
              <p className="text-blue-200 text-sm font-body">
                <strong>Quick Setup:</strong> Create multiple empty scenes at once. You can edit titles and add images later.
              </p>
            </div>

            {/* Range Indicator */}
            <div className="space-y-2">
              <div className="flex justify-between text-xs text-white/50 font-body">
                <span>1 scene</span>
                <span>{numScenes} scenes</span>
                <span>50 max</span>
              </div>
              <input
                type="range"
                min="1"
                max="50"
                value={numScenes}
                onChange={(e) => setNumScenes(parseInt(e.target.value))}
                disabled={isCreating}
                className="w-full h-2 bg-glass-light rounded-lg appearance-none cursor-pointer disabled:opacity-50"
                style={{
                  background: `linear-gradient(to right, rgba(109, 117, 237, 0.5) 0%, rgba(109, 117, 237, 0.5) ${(numScenes / 50) * 100}%, rgba(255, 255, 255, 0.1) ${(numScenes / 50) * 100}%, rgba(255, 255, 255, 0.1) 100%)`
                }}
              />
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="flex-1 glass-button text-white/90 hover:bg-white/10 px-6 py-3 font-ui font-medium"
                disabled={isCreating}
              >
                Cancel
              </button>

              <button
                onClick={handleCreate}
                disabled={isCreating || numScenes < 1}
                className="flex-1 glass-button bg-primary-500/30 hover:bg-primary-500/40 text-white font-ui font-semibold px-6 py-3 flex items-center justify-center space-x-2 disabled:opacity-50"
              >
                {isCreating ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Creating...</span>
                  </>
                ) : (
                  <>
                    <Plus className="w-5 h-5" />
                    <span>Add {numScenes} Scene{numScenes !== 1 ? 's' : ''}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

export default BulkAddScenesDialog

